import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: t('nav.home'), href: "#home" },
    { label: t('nav.about'), href: "#about" },
    { label: t('nav.menu'), href: "#menu" },
    { label: t('nav.gallery'), href: "#gallery" },
    { label: t('nav.contact'), href: "#contact" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        isScrolled || isMobileMenuOpen ? "bg-background/95 backdrop-blur-lg shadow-luxury" : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <a href="#home" className="group relative block hover:opacity-95 transition-opacity">
            <div className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-gold relative px-1 transition-all duration-300 group-hover:scale-[1.02]" style={{ 
              textShadow: '0 0 30px rgba(212, 175, 55, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)',
              filter: 'drop-shadow(0 0 8px rgba(212, 175, 55, 0.3))'
            }}>
              Currylicious Yum Yum
              {/* Elegant underline that glows on hover */}
              <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent opacity-60 group-hover:opacity-100 group-hover:shadow-[0_0_10px_rgba(212,175,55,0.6)] transition-all duration-300"></span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="relative text-foreground hover:text-gold transition-all duration-300 font-medium group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-gold via-gold-light to-gold-accent group-hover:w-full transition-all duration-300" style={{ boxShadow: '0 0 8px hsl(var(--gold) / 0.6)' }}></span>
              </a>
            ))}
            
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-gold hover:text-gold-light" aria-label="Välj språk">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-gold/20 z-50">
                <DropdownMenuItem 
                  onClick={() => setLanguage('sv')}
                  className={`cursor-pointer ${language === 'sv' ? 'bg-gold/10 text-gold' : ''}`}
                >
                  Svenska
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setLanguage('en')}
                  className={`cursor-pointer ${language === 'en' ? 'bg-gold/10 text-gold' : ''}`}
                >
                  English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              variant="default" 
              className="bg-gold hover:bg-gold-light text-background font-semibold"
              onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('nav.bookTable')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-gold hover:text-gold-light" aria-label="Välj språk">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-gold/20 z-50">
                <DropdownMenuItem
                  onClick={() => setLanguage('sv')}
                  className={`cursor-pointer ${language === 'sv' ? 'bg-gold/10 text-gold' : ''}`}
                >
                  Svenska
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('en')}
                  className={`cursor-pointer ${language === 'en' ? 'bg-gold/10 text-gold' : ''}`}
                >
                  English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              className="text-gold"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Meny"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-6 space-y-4 animate-fade-in">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="block text-foreground hover:text-gold transition-colors font-medium py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <Button 
              variant="default" 
              className="w-full bg-gold hover:bg-gold-light text-background font-semibold"
              onClick={() => {
                setIsMobileMenuOpen(false);
                document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('nav.bookTable')}
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
